import React, { useCallback, useMemo, type ReactNode, type RefObject } from 'react';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { modalEntryPoint } from './modalEntryPoint.entrypoint.tsx';
import { MODAL_WIDTH } from './modalWidth.constants.tsx';
import { TemplateLibrarySkeleton } from './TemplateLibrarySkeleton.tsx';

type ModalEntryPointTriggerProps = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	projectId: string;
	projectKey: string;
	containerId?: string;
};

export const ModalEntryPointTrigger = ({
	children,
	projectId,
	projectKey,
	containerId,
}: ModalEntryPointTriggerProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const modalProps: ModalDialogProps = useMemo(
		() => ({
			width: MODAL_WIDTH,
		}),
		[],
	);
	const cloudId = useCloudId();
	const onClose = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'closed', actionSubject: 'modal' }),
			'viewTemplatesLibrary',
		);
	}, [createAnalyticsEvent]);
	const entryPointProps = useMemo(
		() => ({
			projectId,
			onClose,
			projectKey,
			containerId,
		}),
		[onClose, projectId, projectKey, containerId],
	);
	const entryPointParams = useMemo(() => ({ cloudId }), [cloudId]);

	if (!cloudId) {
		return null;
	}

	return (
		<ModalTrigger
			entryPoint={modalEntryPoint}
			modalProps={modalProps}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			preloadOnFocus
			Fallback={TemplateLibrarySkeleton}
		>
			{children}
		</ModalTrigger>
	);
};
